// 主题类型常量 - 避免循环依赖
export const THEME_TYPES = {
  LIGHT: 'light',
  DARK: 'dark'
};

/**
 * 主题工具类
 */
export class ThemeUtils {
  /**
   * 获取当前主题
   * @returns {string} 当前主题类型
   */
  static getCurrentTheme() {
    return localStorage.getItem('app-theme') || THEME_TYPES.LIGHT;
  }

  /**
   * 设置主题
   * @param {string} theme 主题类型
   */
  static setTheme(theme) {
    if (Object.values(THEME_TYPES).includes(theme)) {
      localStorage.setItem('app-theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
      document.documentElement.setAttribute('data-prefers-color-scheme', theme);
      document.documentElement.setAttribute('data-prefers-color', theme);
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
      document.body.classList.add(`theme-${theme}`);
    }
  }

  /**
   * 切换主题
   * @returns {string} 新的主题类型
   */
  static toggleTheme() {
    const currentTheme = this.getCurrentTheme();
    const newTheme = currentTheme === THEME_TYPES.LIGHT ? THEME_TYPES.DARK : THEME_TYPES.LIGHT;
    this.setTheme(newTheme);
    return newTheme;
  }

  /**
   * 检查是否为深色主题
   * @returns {boolean}
   */
  static isDarkTheme() {
    return this.getCurrentTheme() === THEME_TYPES.DARK;
  }

  /**
   * 检查是否为浅色主题
   * @returns {boolean}
   */
  static isLightTheme() {
    return this.getCurrentTheme() === THEME_TYPES.LIGHT;
  }

  /**
   * 获取系统主题偏好
   * @returns {string} 系统主题偏好
   */
  static getSystemThemePreference() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEME_TYPES.DARK;
    }
    return THEME_TYPES.LIGHT;
  }

  /**
   * 监听系统主题变化
   * @param {Function} callback 回调函数
   * @returns {Function} 取消监听的函数
   */
  static watchSystemTheme(callback) {
    if (!window.matchMedia) {
      return () => {};
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const systemTheme = e.matches ? THEME_TYPES.DARK : THEME_TYPES.LIGHT;
      callback(systemTheme);
    };

    mediaQuery.addEventListener('change', handler);
    
    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }

  /**
   * 根据主题获取对应的CSS变量值
   * @param {string} variableName CSS变量名
   * @param {string} theme 主题类型，不传则使用当前主题
   * @returns {string} CSS变量值
   */
  static getThemeVariable(variableName, theme = null) {
    const currentTheme = theme || this.getCurrentTheme();
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    // 尝试获取主题相关的CSS变量
    const themeVariable = `--${currentTheme}-${variableName}`;
    const value = computedStyle.getPropertyValue(themeVariable);
    
    if (value) {
      return value.trim();
    }
    
    // 如果没有找到主题相关的变量，尝试获取通用变量
    const generalVariable = `--${variableName}`;
    return computedStyle.getPropertyValue(generalVariable).trim();
  }

  /**
   * 动态设置CSS变量
   * @param {string} variableName 变量名
   * @param {string} value 变量值
   */
  static setCSSVariable(variableName, value) {
    document.documentElement.style.setProperty(`--${variableName}`, value);
  }

  /**
   * 批量设置主题相关的CSS变量
   * @param {Object} variables 变量对象
   * @param {string} theme 主题类型
   */
  static setThemeVariables(variables, theme = null) {
    const currentTheme = theme || this.getCurrentTheme();
    
    Object.entries(variables).forEach(([key, value]) => {
      const variableName = `${currentTheme}-${key}`;
      this.setCSSVariable(variableName, value);
    });
  }
}

/**
 * 主题相关的常量
 */
export const THEME_CONSTANTS = {
  // 主题类型
  THEME_TYPES,
  
  // 主题存储键名
  STORAGE_KEY: 'app-theme',
  
  // 主题类名前缀
  CLASS_PREFIX: 'theme-',
  
  // 数据属性名
  DATA_ATTRIBUTE: 'data-theme',
  
  // 默认主题
  DEFAULT_THEME: THEME_TYPES.LIGHT,
  
  // 主题切换动画时长
  TRANSITION_DURATION: 300,
};

/**
 * 主题相关的工具函数
 */
export const themeHelpers = {
  /**
   * 生成主题相关的类名
   * @param {string} baseClass 基础类名
   * @param {string} theme 主题类型
   * @returns {string} 完整的类名
   */
  getThemeClassName: (baseClass, theme = null) => {
    const currentTheme = theme || ThemeUtils.getCurrentTheme();
    return `${baseClass} ${THEME_CONSTANTS.CLASS_PREFIX}${currentTheme}`;
  },

  /**
   * 根据主题条件返回不同的值
   * @param {any} lightValue 浅色主题的值
   * @param {any} darkValue 深色主题的值
   * @param {string} theme 主题类型
   * @returns {any} 对应主题的值
   */
  getThemeValue: (lightValue, darkValue, theme = null) => {
    const currentTheme = theme || ThemeUtils.getCurrentTheme();
    return currentTheme === THEME_TYPES.DARK ? darkValue : lightValue;
  },

  /**
   * 检查主题是否匹配
   * @param {string} targetTheme 目标主题
   * @param {string} currentTheme 当前主题
   * @returns {boolean} 是否匹配
   */
  isThemeMatch: (targetTheme, currentTheme = null) => {
    const theme = currentTheme || ThemeUtils.getCurrentTheme();
    return theme === targetTheme;
  },
};

export default ThemeUtils;
